import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import productActions from "../../../../store/actions/items/item.actions";
import { useEffect, useState } from "react";
import TextInputElementComponent from "../../../general/elements/inputElements/TextInputElementComponent";
import textInputModel from "../../../../models/general/elements/inputElements/textInput.model";
import { SkeletonForm } from "../../../general/skeleton";
import useDelayedFlag from "../../../../hooks/useDelayedFlag";
import AlertService from "../../../../services/alerts/alert.service";
import TableElementComponent from "../../../general/elements/TableElementComponent";
import { itemAttributeModel } from "../../../../models/items/attributes/attribute.model";
import validationHelper from "../../../../helpers/validation.helper";

export const UpdateItemAttributesDialogComponent = ({ productActions }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [item, setItem] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [newAttribute, setNewAttribute] = useState(itemAttributeModel);
  const [editMode, setEditMode] = useState(null); // Track which attribute is being edited
  const itemId = searchParams.get("itemId");
  const showSkeleton = useDelayedFlag(!item || loading);

  useEffect(() => {
    if (itemId) {
      fetchItemData();
    }
  }, [itemId]);

  const fetchItemData = async () => {
    setLoading(true);
    try {
      const response = await productActions.getProductById(itemId);
      if (response) {
        setItem(response);
      }
    } catch (error) {
      console.error("Error fetching item:", error);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAttribute({ ...newAttribute, [name]: value });
  };

  const handleEditChange = (e, attributeName) => {
    const updatedAttributes = item.itemAttributes.map((attr) => (attr.attributeName === attributeName ? { ...attr, attributeValue: e.target.value } : attr));
    setItem({ ...item, itemAttributes: updatedAttributes });
  };

  const validateValues = () => {
    const validationErrors = {};
    if (!newAttribute.attributeName) {
      validationErrors.attributeName = "יש להזין שם מאפיין";
    } else if (item.itemAttributes.some((attr) => attr.attributeName === newAttribute.attributeName)) {
      validationErrors.attributeName = "שם המאפיין כבר קיים";
    }
    if (!newAttribute.attributeValue) {
      validationErrors.attributeValue = "יש להזין ערך מאפיין";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleAddNewAttribute = () => {
    if (!validateValues()) return;
    setItem({ ...item, itemAttributes: [...item.itemAttributes, newAttribute] });
    setNewAttribute(itemAttributeModel);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const response = await productActions.updateItemAttributesExtension(item.itemId, item.itemAttributes);
      if (!validationHelper.validateHttpResponse(response)) {
        setErrors({ general: "אירעה שגיאה בעדכון מאפייני פריט. נסה שוב מאוחר יותר." });
        return;
      }
      AlertService.success(`מאפייני פריט ${item.itemId} - ${item.itemDescription} עודכנו בהצלחה`);
    } catch (error) {
      setErrors({ general: "אירעה שגיאה בעדכון מאפייני פריט. נסה שוב מאוחר יותר." });
    }
  };

  const handleDeleteAttribute = (attributeName) => {
    const updatedAttributes = item.itemAttributes.filter((attr) => attr.attributeName !== attributeName);
    setItem({ ...item, itemAttributes: updatedAttributes });
  };

  const handleEditClick = (attributeName) => {
    setEditMode(attributeName);
  };

  const handleEditBlur = () => {
    setEditMode(null);
  };

  const handleEditKeyPress = (e) => {
    if (e.key === "Enter") {
      handleEditBlur();
    }
  };

  const getAttributeNameInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "attributeName",
      error: errors.attributeName,
      value: newAttribute.attributeName,
      onChange: handleInputChange,
      label: "שם מאפיין",
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  const getAttributeValueInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "attributeValue",
      error: errors.attributeValue,
      value: newAttribute.attributeValue,
      onChange: handleInputChange,
      label: "ערך",
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  const getAttributesTableElement = () => {
    const columns = [
      { label: "שם", field: "attributeName" },
      {
        label: "ערך",
        field: "attributeValue",
        render: (value, row) => (editMode === row.attributeName ? <input type="text" value={value} onChange={(e) => handleEditChange(e, row.attributeName)} onBlur={handleEditBlur} onKeyPress={handleEditKeyPress} autoFocus /> : <span onClick={() => handleEditClick(row.attributeName)}>{value}</span>),
      },
    ];

    const actions = [
      {
        label: "מחיקה",
        icon: "fa-solid fa-trash",
        className: "greenButton",
        onClick: (item) => handleDeleteAttribute(item.attributeName),
      },
    ];

    return <TableElementComponent columns={columns} data={item.itemAttributes} actions={actions} />;
  };

  if (!item || loading) {
    return <div className="dialogContainer flex fdColumn gap10">{showSkeleton && <SkeletonForm fields={4} />}</div>;
  }
  return (
    <div className="dialogContainer flex fdColumn gap10">
      <h1>
        מאפייני פריט {item.itemId} - {item.itemDescription}
      </h1>

      <div className="newAttributeContainer flex alignCenter gap10">
        <div>
          {getAttributeNameInputElement()}
          {getAttributeValueInputElement()}
        </div>
        <button className="primaryButton" onClick={handleAddNewAttribute}>
          הוסף
        </button>
      </div>

      <div className="attributesTableContainer">{getAttributesTableElement()}</div>

      <form onSubmit={handleSubmit} className="flex fdColumn gap10">
        <button type="submit" className="primaryButton">
          עדכן
        </button>
        {errors.general && <span className="error">{errors.general}</span>}
      </form>
    </div>
  );
};

const mapStateToProps = (state) => ({
  categories: state.categoryReducer.categories?.items, // Subscribe to categories state from the reducer
});

const mapDispatchToProps = (dispatch) => ({
  productActions: bindActionCreators(productActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UpdateItemAttributesDialogComponent);
